import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Platform,
  Pressable,
  SafeAreaView,
  useWindowDimensions,
} from "react-native";
import Constants from "expo-constants";
import VDBText from "@components/atoms/Text";
import { Container, Spacer } from "@components/atoms";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import useLanguageSelection from "@components/molecules/Languages/LanguageSelector";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import Footer from "@components/common/Footer";
import {
  MenuItemProps,
  MoreScreenSectionDataType,
  MoreScreenSectionType,
} from "./types";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import {
  checkItemConditions,
  defaultSections,
  handleItemSelect,
  useMergeSections,
  useMoreScreenValues,
} from "./utils";
import { useBiometricType } from "../BiometricAuthorization/SupportedBiometrics";
import SectionContainer from "./SectionContainer";
import { View } from "@gluestack-ui/themed";
import { useOtherSearchGroupSlice } from "@redux/slices/otherSearchGroups";
import useMoreScreenModals from "./useMoreScreenModals";
import usePostOnboarding from "../../../hooks/usePostOnboardingData";
import useCurrencySelection from "./useCurrencySelection";
import { useDefaultSearchGroup } from "../../../hooks/useDefaultSearchGroup";
import LayoutContainer from "@components/layout/LayoutContainer";
import { ScopedCopilotProvider } from "@components/CoachOverlay";
import { useCoachOverlay } from "../../../hooks/useCoachOverlay";
import { generateItem } from "./useGenerateItem";
import VDBFooter from "@components/atoms/VDBFooter";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { useGetScreenData } from "../../../hooks/useGetScreenData";
import { ScreenName } from "@utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { BuilderAlertManager } from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import DropDown from "@components/atoms/DropDown";
import { ScreenSizes } from "@src/types/screen";
import { css } from "styled-components/native";
import { getBoxShadow } from "@utils/product/productValidators";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { useOverlayConfig } from "@redux/slices/globalSlice";

type MoreScreenProps = {
  showAsPopover?: boolean;
  onItemClick?: (item: MenuItemProps) => void;
};

const MoreScreen = ({
  showAsPopover = false,
  onItemClick,
}: MoreScreenProps) => {
  const { theme, typography } = useScreenOptionsHooks();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const userInfo = useUserInfo();
  const [sections, setSections] = useState<MoreScreenSectionType[]>([]);
  const { getLanguageByCode } = useLanguageSelection();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const currentLanguage = getLanguageByCode(i18n.language);
  const { selectedGroups } = useSearchGroupSlice();
  const { otherGroups } = useOtherSearchGroupSlice();
  const isFocused = showAsPopover ? true : useIsFocused();
  const { biometricType, supportedBiometrics, isBiometricEnabled } =
    useBiometricType();
  const selectedValues = useMoreScreenValues(biometricType);
  const priceMode = userInfo?.user?.price_mode;
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const scrollRef = useRef();
  const orgSettings = useOrgSettingsSlice();
  const { trackButtonEvent } = useUserAnalytics() || {};
  const { renderModals, showModal } = useMoreScreenModals();
  const [allowPostOnboadingNavigation, setAllowPostOnboadingNavigation] =
    useState(false);
  const appVersion = useSelector((state: any) => state.appState?.appVersion);
  const onNavigationSucceeded = (isSucceeded: boolean) => {
    if (isSucceeded) setAllowPostOnboadingNavigation(false);
  };
  const regularNavigation = showAsPopover ? null : useNavigation();
  const navigation = regularNavigation;
  usePostOnboarding(
    !showAsPopover && allowPostOnboadingNavigation,
    onNavigationSucceeded,
    navigation
  );
  const productTypes = useDefaultSearchGroup();
  const customSections = useGetScreenData(ScreenName.PROFILE);
  const mergedSections = useMergeSections(customSections, defaultSections);
  const { languages, selectedLanguage, changeLanguage } =
    useLanguageSelection();
  const { selectedCurrency, currencies, changeCurrency } =
    useCurrencySelection(navigation);
  const getOverlayConfig = useOverlayConfig();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );

  const bannerVisible = useMemo(() => {
    return (
      (userInfo.isActiveFreeTrial() || userInfo.hasNoPlan()) &&
      !userInfo.isUpcomingPaidPlan() &&
      userInfo?.user?.feature_settings?.display_paid_marketplace_plan_popup
    );
  }, [userInfo]);

  useEffect(() => {
    let updatedSection = mergedSections
      .filter(
        (item) => item.price_modes && item.price_modes.includes(priceMode)
      )
      .map((section) => {
        return {
          ...section,
          data: section.data
            .filter(
              (item) =>
                checkItemConditions(
                  item,
                  selectedValues,
                  userInfo,
                  biometricType,
                  userInfo.isGuest,
                  orgSettings.settings,
                  theme
                ) &&
                item.price_modes &&
                item.price_modes.includes(priceMode) &&
                (item.acceptedScreenSizes
                  ? item.acceptedScreenSizes.includes(
                      theme.isLargeScreen
                        ? ScreenSizes.LARGE
                        : theme.isTablet
                        ? ScreenSizes.TABLET
                        : ScreenSizes.MOBILE
                    )
                  : true)
            )
            .map((item: MoreScreenSectionDataType) => {
              return generateItem(
                theme.isMobile
                  ? item
                  : { ...item, isSelected: item.id === selectedItemId },
                currentLanguage,
                userInfo,
                theme,
                typography,
                selectedValues,
                selectedGroups,
                otherGroups,
                productTypes,
                isBiometricEnabled,
                biometricType,
                t
              );
            }),
        };
      });

    updatedSection = updatedSection.filter(
      (section) => section.data.length > 0
    );
    setSections(updatedSection);
  }, [
    userInfo.user.id,
    selectedValues,
    selectedGroups,
    priceMode,
    isBiometricEnabled,
    selectedItemId,
  ]);

  useEffect(() => {
    if (Platform.OS !== "web") {
      supportedBiometrics();
    }
  }, [isFocused, biometricType]);

  const renderCustomItem = useCallback(
    (
      item: MenuItemProps,
      index: number,
      renderDefaultItem: (item: MenuItemProps, index: number) => JSX.Element
    ) => {
      if (showAsPopover) {
        switch (item.id) {
          case "languages": {
            return (
              <DropDown
                key={item.id}
                title={t("navigation.profile.general.label.language")}
                defaultValue={selectedLanguage?.code}
                options={languages.map((language) => ({
                  label: language?.long_name,
                  value: language.code,
                }))}
                onSelect={changeLanguage}
                showAsPopover={showAsPopover}
              />
            );
          }
          case "currency": {
            return (
              <DropDown
                key={item.id}
                title={t("navigation.profile.general.label.currency")}
                defaultValue={
                  selectedCurrency ? selectedCurrency?.value : undefined
                }
                options={currencies.map((currency) => ({
                  label: currency.currency_symbol + " " + currency.value,
                  value: currency.value,
                }))}
                onSelect={(value) => changeCurrency(value)}
                showAsPopover={showAsPopover}
              />
            );
          }
        }
      }
      return renderDefaultItem(item, index);
    },
    [showAsPopover, selectedLanguage, languages, currencies, selectedCurrency]
  );

  const getMenuContainerProps = useCallback(
    (section: MoreScreenSectionType) =>
      showAsPopover &&
      (section.title === "Language & Currency" ||
        section.title === "languageCurrency")
        ? ({
            display: "flex",
            flexDirection: "row",
            gap: 6,
            backgroundColor: "transparent",
          } as const)
        : {},
    [showAsPopover]
  );

  const { height: windowHeight } = useWindowDimensions();

  // Coach overlay for MoreScreen (always call hook, disable on web via enabled prop)
  const isWeb = Platform.OS === "web";
  const { start: callOverlay, screenConfig: coachScreenConfig } =
    useCoachOverlay({
      screen: "MoreScreen",
      scrollRef,
      autoStart: false,
      enabled:
        !isWeb &&
        !showAsPopover &&
        isFocused &&
        !theme.isLargeScreen &&
        getOverlayConfig?.screens?.MoreScreen &&
        (getOverlayConfig?.screens?.MoreScreen?.overlayActive === undefined ||
          getOverlayConfig?.screens?.MoreScreen?.overlayActive === true),
      // When rendered as a popover (e.g. in the header), MoreScreen lives
      // outside the NavigationContainer, so navigation hooks must be skipped.
      hasNavigationContext: !showAsPopover,
    });

  // Trigger overlay when screen becomes focused (similar to old useEffect in SectionContainer)
  useEffect(() => {
    if (
      !isWeb &&
      !showAsPopover &&
      isFocused &&
      !theme.isLargeScreen &&
      callOverlay
    ) {
      // Small delay to ensure layout is ready
      const timeoutId = setTimeout(() => {
        callOverlay();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isFocused, isWeb, showAsPopover, theme.isLargeScreen, callOverlay]);

  const handleGreetingLongPress = () => {
    const version = process.env.EXPO_PUBLIC_BUILD_DATE
      ? process.env.EXPO_PUBLIC_BUILD_DATE
      : Constants?.expoConfig?.extra?.buildVersion;
    let buildNumber: string | number = "0";
    if (Platform.OS === "ios") {
      buildNumber = (Constants.expoConfig as any)?.ios?.buildNumber || "0";
    } else if (Platform.OS === "android") {
      buildNumber = (Constants.expoConfig as any)?.android?.versionCode || "0";
    }
    Alert.alert("App Version", `${appVersion}(${buildNumber})(${version})`);
  };

  return (
    <ScopedCopilotProvider scope="MoreScreen">
      <LayoutContainer showAsPopover={showAsPopover}>
        <SafeAreaView style={{ flex: 1 }}>
          <StyledScrollView
            ref={scrollRef}
            showAsPopover={showAsPopover}
            {...(theme.isLargeScreen && {
              scrollEnabled: true,
              showsVerticalScrollIndicator: true,
              nestedScrollEnabled: true,
              bounces: false,
              contentContainerStyle: {
                maxHeight: windowHeight - (bannerVisible ? 156 : 115),
              },
            })}
          >
            <Overlay>
              <Container>
                <Spacer size={SpaceType.Big} />
                {!showAsPopover && (
                  <>
                    <Pressable onLongPress={handleGreetingLongPress}>
                      <VDBText
                        type={typography["H1-B"]}
                        color={theme["core-05"]}
                      >
                        {t("more.screenText.hello.text", {
                          username: userInfo.user.name,
                        })}
                      </VDBText>
                    </Pressable>
                    <Spacer size={SpaceType.Big} />
                  </>
                )}
                {sections.map((section, index) => {
                  let filteredData =
                    section.title === "languageCurrency" &&
                    userInfo.lockCurrency()
                      ? section.data.filter((item) => item?.id !== "currency")
                      : section.data;
                  if (Platform.OS === "web") {
                    filteredData = section.data.filter(
                      (item) => item?.id !== "delete_account"
                    );
                  }
                  return filteredData.length > 0 ? (
                    <SectionContainer
                      key={index}
                      title={section?.label}
                      hintText={section?.hint_label}
                      data={filteredData}
                      selectedItemId={selectedItemId}
                      coachScreenConfig={coachScreenConfig}
                      callOverlay={callOverlay}
                      onItemSelect={(item) => {
                        const callback = () => {
                          handleItemSelect(
                            item,
                            dispatch,
                            setSelectedItemId,
                            setAllowPostOnboadingNavigation,
                            onItemClick,
                            showAsPopover,
                            navigation,
                            showModal,
                            theme,
                            trackButtonEvent
                          );
                        };
                        if (enableJewelryBuilder && history.length > 2) {
                          BuilderAlertManager.open(
                            callback,
                            item?.name === "price_mode"
                              ? "reviewPage.stone.details.popUp.priceMode.title.text"
                              : item?.name === "default_search_group"
                              ? "reviewPage.stone.details.popUp.searchGroups.title.text"
                              : ""
                          );
                        } else {
                          callback();
                        }
                      }}
                      showAsPopover={showAsPopover}
                      renderCustomItem={renderCustomItem}
                      menuContainerProps={getMenuContainerProps(section)}
                      direction={section?.direction}
                    />
                  ) : null;
                })}
                {!userInfo?.isBusinessPlan() &&
                  (!showAsPopover ? (
                    <Footer />
                  ) : (
                    <>
                      <VDBFooter />
                      <Spacer size={SpaceType.Basic} />
                    </>
                  ))}
                {Platform.OS === "android" && (
                  <Spacer size={SpaceType.ExtraLarge} />
                )}
                {!showAsPopover && renderModals()}
              </Container>
            </Overlay>
          </StyledScrollView>
        </SafeAreaView>
      </LayoutContainer>
    </ScopedCopilotProvider>
  );
};

const Overlay = styled(View)<any>`
  margin-bottom: ${({ theme }) =>
    theme.isMobile || theme.isTablet
      ? Platform.OS === "ios"
        ? 100
        : Platform.OS === "android"
        ? 150
        : theme.isLargeScreen
        ? SpaceType.Double
        : 100
      : Platform.OS === "ios"
      ? 140
      : Platform.OS === "android"
      ? 50
      : 0}px;
`;

const StyledScrollView = styled.ScrollView<{ showAsPopover: boolean }>`
  background-color: ${({ theme }) => theme["core-02"]};
  ${({ showAsPopover }) =>
    showAsPopover &&
    css`
      border-width: 1px;
      border-color: ${({ theme }) => theme["core-03"]};
      box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Normal)};
    `}
`;

export default MoreScreen;
